import type { AgencyGraphJson, AgencyResponse } from '@saaga/shared/interfaces/agency.interface'
import { agentsApiSlice } from '@saaga/shared/store/agents-api.store'
import type { AppDispatch } from '@saaga/shared/store/store'
import { logger } from '@saaga/shared/utils/logger.util'
import { toast } from 'sonner'

import type { AgencyFormSchemaType } from '@/components/agencies/agency-form.schema'

import {
  buildVisibilityPayload,
  mapFormToCreatePayload,
  mapFormToUpdatePayload,
} from './agency-form-mapper.util'

export interface AgencyMutationFns {
  updateFn: (params: { slug: string; payload: ReturnType<typeof mapFormToUpdatePayload> }) => {
    unwrap: () => Promise<AgencyResponse>
  }
  createFn: (payload: ReturnType<typeof mapFormToCreatePayload>) => {
    unwrap: () => Promise<AgencyResponse>
  }
}

const invalidateAgencyTags = (dispatch: AppDispatch, slug: string): void => {
  dispatch(
    agentsApiSlice.util.invalidateTags([
      { type: 'Agencies', id: 'LIST' },
      { type: 'Agencies', id: 'RECENTLY_USED' },
      { type: 'Agencies', id: slug },
    ])
  )
}

export const saveAgencyDraft = async (
  slug: string,
  values: AgencyFormSchemaType,
  flowData: AgencyGraphJson,
  fns: AgencyMutationFns,
  dispatch: AppDispatch
): Promise<void> => {
  const contentPayload = mapFormToUpdatePayload(values, flowData)
  const visibilityPayload = buildVisibilityPayload('draft')
  await fns.updateFn({ slug, payload: { ...contentPayload, ...visibilityPayload } }).unwrap()
  invalidateAgencyTags(dispatch, slug)
  toast.success('Agency draft saved')
}

export const publishAgencyToOrg = async (
  slug: string,
  values: AgencyFormSchemaType,
  flowData: AgencyGraphJson,
  fns: AgencyMutationFns,
  dispatch: AppDispatch
): Promise<void> => {
  const contentPayload = mapFormToUpdatePayload(values, flowData)
  const visibilityPayload = buildVisibilityPayload('private')
  await fns.updateFn({ slug, payload: { ...contentPayload, ...visibilityPayload } }).unwrap()
  invalidateAgencyTags(dispatch, slug)
  toast.success('Agency published to organization')
}

export const submitAgencyForReview = async (
  slug: string,
  values: AgencyFormSchemaType,
  flowData: AgencyGraphJson,
  fns: AgencyMutationFns,
  dispatch: AppDispatch
): Promise<void> => {
  const contentPayload = mapFormToUpdatePayload(values, flowData)
  const visibilityPayload = buildVisibilityPayload('pending_review')
  await fns.updateFn({ slug, payload: { ...contentPayload, ...visibilityPayload } }).unwrap()
  invalidateAgencyTags(dispatch, slug)
  toast.success('Agency submitted for review')
}

export const createNewAgency = async (
  values: AgencyFormSchemaType,
  flowData: AgencyGraphJson,
  fns: AgencyMutationFns
): Promise<string> => {
  const payload = mapFormToCreatePayload(values, flowData)
  const response = await fns.createFn(payload).unwrap()
  toast.success('Agency created successfully')
  return response.slug
}

export const handleSaveError = (error: unknown, isEditMode: boolean): void => {
  logger.error('Failed to save agency:', error)
  toast.error(isEditMode ? 'Failed to update agency' : 'Failed to create agency')
}
